/** @jsxImportSource @emotion/react */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { css, jsx } from '@emotion/react';
import Textfield, { Theme } from 'components/textfield';
import Select, { CountrySelect, VatCodeSelect } from 'components/select';
import { Grid, GridColumn, PageWrapper } from 'components/page';
import {
  Field,
  Form,
  FieldArray,
  useForm,
  MoneyInput,
  FormHeader,
  FormSection,
  VatIdentificationNumberInput,
} from 'components/form';
import Textarea from '@atlaskit/textarea';
import { FormRenderProps } from 'react-final-form';
import { FormattedMessage, FormattedNumber, useIntl } from 'components/i18n';
import { CompanyAvatar } from '../../components/avatar';
import { RadioGroup } from '@atlaskit/radio';
import { customerType } from './messages';
import ImageField from '../../components/form/ImageField';
import Button from '../../components/button';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { FormFooter } from '@atlaskit/form';
import TextAreaWithVariables from './components/TextAreaWithVariables';
import { SketchPicker } from 'react-color';
import FieldRange from '@atlaskit/range';
import ColorPicker from '../../components/color-picker/ColorPicker';
import RadioGroupField from '../../components/form/RadioGroupField';
import { ButtonGroup } from '@atlaskit/button';
import { useNavigate } from 'react-router';

export interface Props extends FormRenderProps<any> {
  isSaving?: boolean;
}

const invoiceTemplateOptions = [
  {
    label: 'Template 1',
    value: '1',
  },
  {
    label: 'Template 2',
    value: '2',
  },
  {
    label: 'Template 3',
    value: '3',
  },
];

const companyInformationPositionOptions = [
  {
    label: <FormattedMessage id="common.top" defaultMessage="Top" />,
    value: 'top',
  },
  {
    label: <FormattedMessage id="common.bottom" defaultMessage="Bottom" />,
    value: 'bottom',
  },
];

const logoPositionOptions = [
  {
    label: <FormattedMessage id="common.left" defaultMessage="Left" />,
    value: 'left',
  },
  {
    label: <FormattedMessage id="common.right" defaultMessage="Right" />,
    value: 'right',
  },
];

const customerPositionOptions = [
  {
    label: <FormattedMessage id="common.left" defaultMessage="Left" />,
    value: 'left',
  },
  {
    label: <FormattedMessage id="common.right" defaultMessage="Right" />,
    value: 'right',
  },
];

const invoiceInformationLayoutOptions = [
  {
    label: <FormattedMessage id="common.vertical" defaultMessage="Vertical" />,
    value: 'vertical',
  },
  {
    label: (
      <FormattedMessage id="common.horizontal" defaultMessage="Horizontal" />
    ),
    value: 'horizontal',
  },
];

const InvoiceSettingsForm: React.FC<Props> = ({
  values: sale,
  handleSubmit,
  // isSaving,
  pristine,
  form: {
    mutators: { push, pop },
    change,
    getState,
    reset,
  },
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const saveFooter = (
    <FormFooter align="start">
      <ButtonGroup>
        <Button appearance="primary" type="submit">
          <FormattedMessage
            id="sale.InvoiceSettingsForm.save"
            defaultMessage="Save"
          />
        </Button>
        <Button appearance="subtle" onClick={() => reset()}>
          <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
        </Button>
      </ButtonGroup>
    </FormFooter>
  );

  // const tabs = useMemo(
  //   () => [
  //     {
  //
  //       ),
  //     },
  //   ],
  //   [intl]
  // );

  return (
    <form
      onSubmit={handleSubmit}
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <div
        css={css`
          flex: 1;
          margin-left: -6px;
        `}
      >
        <Tabs
          // onSelect={(tab, index)=> {
          //   navigate(tab.path);
          // }}
          // tabs={tabs}
          // components={{ Content: (data) => <div>{data.data.content}</div> }}
        >
          <TabList>
            <Tab><FormattedMessage
              id='sale.InvoiceSettingsForm.info'
              defaultMessage='Info'/>
            </Tab>
            <Tab><FormattedMessage
              id='sale.InvoiceSettingsForm.layout'
              defaultMessage='Layout'/>
            </Tab>
            <Tab><FormattedMessage
              id='sale.InvoiceSettingsForm.email'
              defaultMessage='Email'/></Tab>
            <Tab>
              <FormattedMessage
                id='sale.InvoiceSettingsForm.recurringEmail'
                defaultMessage='Recurring email'/>
            </Tab>
          </TabList>
          <TabPanel>
              <div
              css={css`
              max-width: 400px;
            `}
              >
              <FormSection
              title={
              <FormattedMessage
              id="sale.InvoiceSettingsForm.companyInformation"
              defaultMessage="Company information"
              />
            }
              >
              <Field
              isRequired
              label={
              <FormattedMessage
              id="sale.InvoiceSettingsForm.companyName"
              defaultMessage="Company name"
              />
            }
              name="companyInformation.name"
              component={Textfield}
              />
              <Field
              isRequired
              label={
              <FormattedMessage
              id="sale.CustomerForm.address.line1"
              defaultMessage="Line 1"
              />
            }
              name="companyInformation.address.line1"
              >
              <Textfield />
              </Field>
              <Field
              label={
              <FormattedMessage
              id="sale.CustomerForm.address.line2"
              defaultMessage="Line 2"
              />
            }
              name="companyInformation.address.line2"
              >
              <Textfield />
              </Field>

              <Grid spacing="cosy">
              <GridColumn medium={4}>
              <Field
              isRequired
              label={
              <FormattedMessage
              id="sale.CustomerForm.address.postalCode"
              defaultMessage="Postal code"
              />
            }
              name="companyInformation.address.postalCode"
              >
              <Textfield />
              </Field>
              </GridColumn>
              <GridColumn medium={4}>
              <Field
              isRequired
              label={
              <FormattedMessage
              id="sale.CustomerForm.address.city"
              defaultMessage="City"
              />
            }
              name="companyInformation.address.city"
              >
              <Textfield />
              </Field>
              </GridColumn>
              <GridColumn medium={4}>
              <Field
              isRequired
              label={
              <FormattedMessage
              id="sale.CustomerForm.address.country"
              defaultMessage="Country"
              />
            }
              name="companyInformation.address.country"
              >
              <CountrySelect />
              </Field>
              </GridColumn>
              </Grid>
              <Grid spacing="cosy">
              <GridColumn>
              <Field
              label={
              <FormattedMessage
              id="sale.CustomerForm.phone"
              defaultMessage="Phone"
              />
            }
              component={Textfield}
              name="companyInformation.phone"
              />
              </GridColumn>
              <GridColumn>
              <Field
              label={
              <FormattedMessage
              id="sale.CustomerForm.email"
              defaultMessage="Email"
              />
            }
              component={Textfield}
              name="companyInformation.email"
              />
              </GridColumn>
              </Grid>
              <Field
              label={
              <FormattedMessage
              id="sale.CustomerForm.website"
              defaultMessage="Website"
              />
            }
              width="medium"
              name="companyInformation.website"
              >
              <Textfield />
              </Field>
              <Field
              label={
              <FormattedMessage
              id="sale.CustomerForm.companyRegistrationNumber"
              defaultMessage="Company registration number"
              />
            }
              width="medium"
              name="companyInformation.companyRegistrationNumber"
              >
              <Textfield />
              </Field>
              <Field
              label={
              <FormattedMessage
              id="sale.CustomerForm.vatNumber"
              defaultMessage="VAT ID number"
              />
            }
              // validate={vatValidate}
              name="companyInformation.vatIdentificationNumber"
              placeholder={intl.formatMessage({
              id: 'sale.CustomerForm.vatNumberPlaceholder',
              defaultMessage: 'Country code + number',
            })}
              width="medium"
              component={VatIdentificationNumberInput}
              />
              <Field
              label={
              <FormattedMessage
              id="sale.CustomerForm.bankAccountNumber"
              defaultMessage="Account number"
              />
            }
              width="medium"
              placeholder="IBAN"
              name="companyInformation.bankAccountNumber"
              >
              <Textfield />
              </Field>
              </FormSection>
              </div>
          </TabPanel>
          <TabPanel>
            <div
              css={css`
              width: 100%;
            `}
            >
              <div
                css={css`
                max-width: 400px;
              `}
              >
                <FormSection
                  title={
                    <FormattedMessage
                      id="sale.InvoiceSettingsForm.template"
                      defaultMessage="Template"
                    />
                  }
                >
                  <RadioGroupField
                    label={
                      <FormattedMessage
                        id="sale.InvoiceSettingsForm.invoiceTemplate"
                        defaultMessage="Invoice template"
                      />
                    }
                    name="invoiceTemplateId"
                    options={invoiceTemplateOptions}
                  />
                  <Field
                    label={
                      <FormattedMessage
                        id="sale.InvoiceSettingsForm.accentColor"
                        defaultMessage="Accent color"
                      />
                    }
                    width="small"
                    name="accentColor"
                    component={ColorPicker}
                  />
                  <Grid spacing="cosy">
                    <GridColumn medium={8}>
                      <Field
                        label={
                          <FormattedMessage
                            id="sale.InvoiceSettingsForm.fontSize"
                            defaultMessage="Font size"
                          />
                        }
                        name="fontSize"
                        min={8}
                        step={1}
                        max={11}
                        component={FieldRange}
                      />
                    </GridColumn>
                    <GridColumn medium={4}>
                      {/*<Field*/}
                      {/*  label={*/}
                      {/*    <FormattedMessage*/}
                      {/*      id="sale.InvoiceSettingsForm.font"*/}
                      {/*      defaultMessage="Font"*/}
                      {/*    />*/}
                      {/*  }*/}
                      {/*  name="fontFamily"*/}
                      {/*>*/}
                      {/*  {({ input }) => (*/}
                      {/*    <Select*/}
                      {/*      options={['Helvetica', 'Arial','Calibri'].map((option) => ({*/}
                      {/*        key: option,*/}
                      {/*        value: option,*/}
                      {/*      }))}*/}
                      {/*      getOptionLabel={(option) => option.key}*/}
                      {/*      getOptionValue={(option) => option.value}*/}
                      {/*      /* @ts-ignore */}
                      {/*      onChange={(option) => input.onChange(option.value)}*/}
                      {/*      value={{ key: input.value, value: input.value }}*/}
                      {/*    />*/}
                      {/*  )}*/}
                      {/*</Field>*/}
                    </GridColumn>
                  </Grid>
                </FormSection>
                <FormSection
                  title={
                    <FormattedMessage
                      id="sale.InvoiceSettingsForm.logo"
                      defaultMessage="Logo"
                    />
                  }
                >
                  <Grid spacing="cosy">
                    <GridColumn medium={8}>
                      <ImageField
                        onImageSelect={(image: any) => {
                          change('logo', image);
                        }}
                        placeHolder={
                          <FormattedMessage
                            id="sale.InvoiceSettingsForm.uploadLogo"
                            defaultMessage="Upload your logo"
                          />
                        }
                        label={
                          <FormattedMessage
                            id="sale.InvoiceSettingsForm.logo"
                            defaultMessage="Logo"
                          />
                        }
                        name="logoUrl"
                      />
                    </GridColumn>
                    <GridColumn medium={4} />
                    <GridColumn medium={8}>
                      <Field
                        label={
                          <FormattedMessage
                            id="sale.InvoiceSettingsForm.textLogo"
                            defaultMessage="Text logo"
                          />
                        }
                        name="textLogo"
                        rows={1}
                        component={Textarea}
                      />
                    </GridColumn>
                    <GridColumn medium={4}>
                      <Field
                        label={
                          <FormattedMessage
                            id="sale.InvoiceSettingsForm.textLogoColor"
                            defaultMessage="Text logo color"
                          />
                        }
                        name="textLogoColor"
                        component={ColorPicker}
                      />
                    </GridColumn>
                    <GridColumn medium={8}>
                      <Field
                        label={
                          <FormattedMessage
                            id="sale.InvoiceSettingsForm.logoSize"
                            defaultMessage="Logo size"
                          />
                        }
                        name="logoHeight"
                        min={20}
                        max={120}
                        component={FieldRange}
                      />
                    </GridColumn>
                  </Grid>
                </FormSection>
                <div
                  css={css`
                  max-width: 256px;
                `}
                >
                  <FormSection
                    title={
                      <FormattedMessage
                        id="sale.InvoiceSettingsForm.positioning"
                        defaultMessage="Positioning"
                      />
                    }
                  >
                    <RadioGroupField
                      label={
                        <FormattedMessage
                          id="sale.InvoiceSettingsForm.logoPosition"
                          defaultMessage="Logo position"
                        />
                      }
                      name="logoPosition"
                      options={logoPositionOptions}
                    />
                    <Field
                      label={
                        <FormattedMessage
                          id="sale.InvoiceSettingsForm.logoMarginTop"
                          defaultMessage="Logo margin top"
                        />
                      }
                      name="logoMarginTop"
                      min={0}
                      step={0.1}
                      max={8}
                      component={FieldRange}
                    />
                    <RadioGroupField
                      label={
                        <FormattedMessage
                          id="sale.InvoiceSettingsForm.companyInformationPosition"
                          defaultMessage="Company information position"
                        />
                      }
                      type="number"
                      name="companyInformationPosition"
                      options={companyInformationPositionOptions}
                      onChange={(e) => {
                        const position = e.target.value;
                        console.log('position', position);
                        if (position === 'top') {
                          change('invoiceInformationMarginTop', 3);
                          // change('descriptionMarginTop', 3);
                        } else if (position === 'bottom') {
                          change('invoiceInformationMarginTop', 0);
                          // change('descriptionMarginTop', 3);
                        }
                      }}
                    />
                    <RadioGroupField
                      label={
                        <FormattedMessage
                          id="sale.InvoiceSettingsForm.invoiceInformationLayout"
                          defaultMessage="Invoice information layout"
                        />
                      }
                      name="invoiceInformationLayout"
                      options={invoiceInformationLayoutOptions}
                    />
                    <Field
                      label={
                        <FormattedMessage
                          id="sale.InvoiceSettingsForm.invoiceInformationMarginTop"
                          defaultMessage="Invoice information margin top"
                        />
                      }
                      name="invoiceInformationMarginTop"
                      min={0}
                      step={0.1}
                      max={10}
                      component={FieldRange}
                    />
                    <RadioGroupField
                      label={
                        <FormattedMessage
                          id="sale.InvoiceSettingsForm.customerPosition"
                          defaultMessage="Customer position"
                        />
                      }
                      type="number"
                      name="customerPosition"
                      options={customerPositionOptions}
                    />
                    <Field
                      label={
                        <FormattedMessage
                          id="sale.InvoiceSettingsForm.descriptionMarginTop"
                          defaultMessage="Description margin top"
                        />
                      }
                      name="descriptionMarginTop"
                      min={0}
                      step={0.1}
                      max={10}
                      component={FieldRange}
                    />
                    <Field
                      label={
                        <FormattedMessage
                          id="sale.InvoiceSettingsForm.noteMarginTop"
                          defaultMessage="Note margin top"
                        />
                      }
                      name="noteMarginTop"
                      min={0}
                      step={0.1}
                      max={12}
                      component={FieldRange}
                    />
                  </FormSection>
                </div>
              </div>
              <FormSection
                css={css`
                margin-top: 3rem;
              `}
                title={
                  <FormattedMessage
                    id="sale.InvoiceSettingsForm.defaultText"
                    defaultMessage="Default text"
                  />
                }
              >
                <Grid spacing="cosy">
                  <GridColumn medium={12}>
                    <Field
                      label={
                        <FormattedMessage
                          id="sale.InvoiceSettingsForm.defaultDescription"
                          defaultMessage="Default description"
                        />
                      }
                      name="defaultDescription"
                      component={TextAreaWithVariables}
                      placeholder={intl.formatMessage({
                        id:
                          'sale.InvoiceSettingsForm.defaultDescriptionPlaceholder',
                        defaultMessage: `Subject\nProject ......`,
                      })}
                      rows={1}
                      minimumRows={1}
                    />
                  </GridColumn>
                </Grid>
                <Grid spacing="cosy">
                  <GridColumn medium={12}>
                    <Field
                      label={
                        <FormattedMessage
                          id="sale.InvoiceSettingsForm.defaultNote"
                          defaultMessage="Default note"
                        />
                      }
                      name="defaultNote"
                      component={TextAreaWithVariables}
                      minimumRows={4}
                    />
                  </GridColumn>
                </Grid>
                <Grid spacing="cosy">
                  <GridColumn medium={12}>
                    <Field
                      label={
                        <FormattedMessage
                          id="sale.InvoiceSettingsForm.defaultFooter"
                          defaultMessage="Default footer"
                        />
                      }
                      name="defaultFooter"
                      component={TextAreaWithVariables}
                      minimumRows={1}
                      rows={1}
                    />
                  </GridColumn>
                </Grid>
              </FormSection>
            </div>
          </TabPanel>

          <TabPanel>
              <div
              css={css`
              width: 100%;
            `}
              >
              <FormSection
              css={css`
                margin-top: 3rem;
              `}
              title={
              <FormattedMessage
              id="sale.InvoiceSettingsForm.emailTemplate"
              defaultMessage="Email template"
              />
            }
              >
              <Field
              name="emailTemplate.bcc"
              label={
              <FormattedMessage
              id="sale.InvoiceSettingsForm.email.bcc"
              defaultMessage="BCC"
              />
            }
              component={Textfield}
              />
              <Field
              name="emailTemplate.subject"
              label={
              <FormattedMessage
              id="sale.InvoiceSettingsForm.email.subject"
              defaultMessage="Subject"
              />
            }
              rows={1}
              minimumRows={1}
              component={TextAreaWithVariables}
              />
              <Field
              name="emailTemplate.body"
              label={
              <FormattedMessage
              id="sale.InvoiceSettingsForm.email.body"
              defaultMessage="Message"
              />
            }
              component={TextAreaWithVariables}
              minimumRows={8}
              />
              </FormSection>
              </div>
          </TabPanel>
          <TabPanel>
            <div
              css={css`
              width: 100%;
            `}
            >
              {' '}
              <FormSection
                title={
                  <FormattedMessage
                    id="sale.InvoiceSettingsForm.recurringEmailTemplate"
                    defaultMessage="Recurring email template"
                  />
                }
              >
                <Field
                  name="recurringEmailTemplate.bcc"
                  label={
                    <FormattedMessage
                      id="sale.InvoiceSettingsForm.email.bcc"
                      defaultMessage="BCC"
                    />
                  }
                  component={Textfield}
                />
                <Field
                  name="recurringEmailTemplate.subject"
                  label={
                    <FormattedMessage
                      id="sale.InvoiceSettingsForm.email.subject"
                      defaultMessage="Subject"
                    />
                  }
                  rows={1}
                  minimumRows={1}
                  component={TextAreaWithVariables}
                />
                <Field
                  name="recurringEmailTemplate.body"
                  label={
                    <FormattedMessage
                      id="sale.InvoiceSettingsForm.email.body"
                      defaultMessage="Message"
                    />
                  }
                  component={TextAreaWithVariables}
                  minimumRows={8}
                />
              </FormSection>
            </div>
          </TabPanel>
        </Tabs>
      </div>
      {saveFooter}
    </form>
  );
};

export default InvoiceSettingsForm;
